// Facilities — list page
// AISC 207-25 GR §4.14–4.17 closure (Multi-Site auditing).
// Backend deployed 2026-05-05 (Wave A migration 019); SPA route wired 2026-05-07.

import React, { useEffect, useState, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import PageGuide from "../../components/PageGuide";
import {
  Container, Stack, Typography, Breadcrumbs, Paper, Box, Alert,
  TableHead, TableRow, TableCell, TableBody, TableContainer,
  CircularProgress,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";

import { facilitiesApi } from "../../utils/facilitiesApi";

import SmartTable from "../../components/SmartTable/SmartTable";
export default function Facilities() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await facilitiesApi.list();
      setRows(res?.data?.data || res?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed to load facilities");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Container maxWidth="xl">
      <Helmet><title>Facilities | SFM</title></Helmet>
      <Breadcrumbs sx={{ mb: 1 }}>
        <RouterLink to="/dashboard/app" style={{ color: "inherit" }}>Home</RouterLink>
        <Typography color="text.primary">Facilities</Typography>
      </Breadcrumbs>

      <Typography variant="h4" sx={{ mb: 1 }}>Facilities</Typography>
      <PageGuide
        why="Keep a register of each shop and yard you operate — so multi-site work is traceable to where it was done."
        who="The QM, registering each shop and yard for multi-site traceability."
        what="Your list of physical sites (shops, yards) audited under one quality system."
        how="Add each site with its name, code, and address; site-level records reference it."
        when="When you operate more than one location, or to strengthen audit-day traceability for your primary site."
        standard="AISC 207-25 GR §4.14–4.17"
      />

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <SmartTable tableId="facilities" lockedColumns={["Name"]} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Site Code</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Tenant</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={5} sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                  No facilities recorded. Per GR §4.14 a tenant operating from a single site is not required to maintain
                  a facilities register, but adding the primary site here strengthens audit-day traceability.
                </TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell><strong>{r.name || "—"}</strong></TableCell>
                  <TableCell>{r.site_code || "—"}</TableCell>
                  <TableCell>{r.address || "—"}</TableCell>
                  <TableCell>{r.tenant_id || "—"}</TableCell>
                  <TableCell>{r.created_at || r.createdAt || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </SmartTable>
        </TableContainer>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        {rows.length} facilit{rows.length === 1 ? "y" : "ies"} on file. AISC 207-25 GR §4.14–4.17 evidence.
      </Typography>
    </Container>
  );
}
